import { useCallback, useRef } from "react";

export function useSingleActiveVideo() {
  const activeVideoIdRef = useRef(null);
  const videoRegistryRef = useRef(new Map());

  const registerVideo = useCallback((videoId, element) => {
    if (element) {
      videoRegistryRef.current.set(videoId, element);
      return;
    }

    videoRegistryRef.current.delete(videoId);

    if (activeVideoIdRef.current === videoId) {
      activeVideoIdRef.current = null;
    }
  }, []);

  const playVideo = useCallback((videoId) => {
    const nextActiveVideo = videoRegistryRef.current.get(videoId);

    if (!nextActiveVideo) {
      return;
    }

    const activeVideoId = activeVideoIdRef.current;

    if (activeVideoId && activeVideoId !== videoId) {
      const previousActiveVideo = videoRegistryRef.current.get(activeVideoId);
      previousActiveVideo?.pause();
    }

    activeVideoIdRef.current = videoId;
  }, []);

  const clearActiveVideo = useCallback((videoId) => {
    if (activeVideoIdRef.current === videoId) {
      activeVideoIdRef.current = null;
    }
  }, []);

  const pauseAll = useCallback(() => {
    const activeVideoId = activeVideoIdRef.current;
    if (activeVideoId) {
      const activeVideo = videoRegistryRef.current.get(activeVideoId);
      activeVideo?.pause();
      activeVideoIdRef.current = null;
    }
  }, []);

  const getVideoControlProps = useCallback(
    (videoId) => ({
      registerVideoRef: (element) => registerVideo(videoId, element),
      onVideoPlay: () => playVideo(videoId),
      onVideoPause: () => clearActiveVideo(videoId),
      onVideoEnded: () => clearActiveVideo(videoId),
    }),
    [clearActiveVideo, playVideo, registerVideo],
  );

  return {
    pauseAll,
    getVideoControlProps,
  };
}
